import styled, { css, keyframes } from 'styled-components';

const scrollLeft = keyframes`
  from { transform: translateX(0); }
  to { transform: translateX(-50%); }
`;

const scrollRight = keyframes`
  from { transform: translateX(-50%); }
  to { transform: translateX(0); }
`;

/* On white (the default): the marks are flattened to grey and the heading is
   quiet. It never shares a container or a heading with the audit figures, so
   no logo reads as the source of a figure. */
export const Wrapper = styled.section`
  position: relative;
  z-index: 1;
  padding: 28px 0 56px;
  background: #fff;
  scroll-margin-top: 94px;

  @media (max-width: 1199px) {
    scroll-margin-top: 63px;
  }
  @media (max-width: 767px) {
    padding: 20px 0 40px;
  }

  /* Inside the hero, under the band of audit figures: no white fill, the
     marks and the heading turn white on the hero's blue. A rule of its own
     (the same as over the audit band) and the air over it keep the two
     apart; the wedge at the foot of the hero gives the air under it. */
  ${({ $onHero }) =>
    $onHero &&
    css`
      padding: 0 0 8px;
      background: none;

      &::before {
        content: '';
        display: block;
        margin: 0 5vw 28px;
        border-top: 1px solid rgba(255, 255, 255, 0.28);
        @media (max-width: 991px) {
          margin: 0 24px 24px;
        }
        @media (max-width: 767px) {
          margin: 0 30px 22px;
        }
      }

      /* && outranks the rules of Heading and Item, defined further down */
      && ${Heading} {
        color: rgba(255, 255, 255, 0.86);
        @media (max-width: 767px) {
          color: rgba(255, 255, 255, 0.92);
        }
      }
      && ${Item} {
        color: #fff;
        opacity: 0.6;
      }
      && ${Item}:hover {
        opacity: 1;
      }
      && ${Item} img {
        filter: brightness(0) invert(1);
      }
      && ${Item} img[data-plain] {
        filter: grayscale(1) invert(1);
      }
    `}
`;

export const Heading = styled.h2`
  margin: 0 0 22px;
  padding: 0 16px;
  color: #5d646d;
  font-size: 16px;
  font-weight: 500;
  line-height: 1.4;
  letter-spacing: 0;
  text-align: center;

  @media (max-width: 767px) {
    margin-bottom: 20px;
  }
`;

export const Rows = styled.div`
  --logo-h: 24px;
  --item-gap: 56px;
  --row-h: 40px;
  overflow: hidden;
  -webkit-mask-image: linear-gradient(90deg, transparent, #000 8%, #000 92%, transparent);
  mask-image: linear-gradient(90deg, transparent, #000 8%, #000 92%, transparent);

  @media (max-width: 767px) {
    --logo-h: 22px;
    --item-gap: 40px;
    --row-h: 36px;
  }
`;

export const Row = styled.div`
  overflow: hidden;
  height: var(--row-h);

  & + & {
    margin-top: 20px;
  }
`;

export const Track = styled.div`
  display: flex;
  width: max-content;
  height: 100%;
  will-change: transform;
  animation: ${({ $reverse }) => ($reverse ? scrollRight : scrollLeft)} 55s linear infinite;

  &:hover,
  &:focus-within {
    animation-play-state: paused;
  }

  /* Reduced motion: the same two rows, standing still (no wrapped grid, which
     would turn the band into a wall of logos at the bottom of the first
     screen). */
  @media (prefers-reduced-motion: reduce) {
    animation: none;
    will-change: auto;
  }
`;

export const List = styled.ul`
  display: flex;
  flex: none;
  align-items: center;
  height: 100%;
  margin: 0;
  padding: 0;
  list-style: none;
`;

export const Item = styled.li`
  display: flex;
  flex: none;
  align-items: center;
  height: var(--row-h);
  margin: 0;
  padding: 0 calc(var(--item-gap) / 2);
  color: #0f2137;
  opacity: 0.5;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 1;
  }

  && img {
    display: block;
    width: auto;
    height: calc(var(--logo-h) * var(--s, 1));
    max-width: none;
    filter: brightness(0);
  }

  /* Marks drawn as light letters on dark tiles would turn into a solid block,
     so the tiles only lose their colour. */
  && img[data-plain] {
    filter: grayscale(1);
  }

  span {
    font-size: calc(var(--logo-h) * 0.72 * var(--s, 1));
    font-weight: 700;
    letter-spacing: -0.01em;
    line-height: 1;
    white-space: nowrap;
  }

  small {
    margin-left: 6px;
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.02em;
  }

`;
